<template>
    <div class="admin-instance-page">
        <div class="nk-block-head nk-block-head-sm">
            <div class="nk-block-between align-items-start">
                <div class="nk-block-head-content admin-instance-list__intro">
                    <div class="admin-instance-list__title-row">
                        <h3 class="nk-block-title page-title mb-0">{{trans('plan.plan')}}</h3>
                    </div>
                    <div class="nk-block-des text-soft" v-if="!plans.total">
                        <p>{{trans('plan.module_info_description')}}</p>
                    </div>
                </div>
                <div class="nk-fmg-actions flex-shrink-0">
                    <ul class="nk-block-tools g-2 flex-wrap justify-content-end">
                        <li><a href="#" class="btn btn-sm admin-toolbar-btn admin-toolbar-btn--outline" role="button" aria-controls="plan-filter-panel" :aria-expanded="showFilterPanel ? 'true' : 'false'" @click.prevent="showFilterPanel = !showFilterPanel" @keydown.space.prevent="showFilterPanel = !showFilterPanel"><admin-icon name="sliders" aria-hidden="true"></admin-icon> <span>{{trans('general.filter')}}</span></a></li>
                        <li class="nk-block-tools-opt d-sm-block">
                            <button type="button" class="btn btn-sm admin-toolbar-btn admin-toolbar-btn--solid" v-if="hasPermission('create-currency')" v-b-modal.modal-1>
                                <admin-icon name="plus"></admin-icon> {{ trans('general.add_new') }}
                            </button>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
        
        <transition name="fade">
            <div id="plan-filter-panel" class="card admin-instance-form-card mb-2" v-if="showFilterPanel">
                <div class="card-body">
                    <div class="admin-instance-card-head">
                        <h4 class="admin-instance-card-head__title">{{trans('general.filter')}}</h4>
                        <p class="admin-instance-card-head__hint">{{ trans('plan.plan') }}</p>
                    </div>
                    <div class="row">
                        <div class="col-6 col-md-4">
                            <div class="form-group">
                                <label for="">{{trans('plan.name')}}</label>
                                <input class="form-control" name="name" v-model="filterPlanForm.name">
                            </div>
                        </div>
                        <div class="col-6 col-md-4">
                            <div class="form-group">
                                <label for="">{{trans('general.sort_by')}}</label>
                                <select name="order" class="form-control" v-model="filterPlanForm.sort_by">
                                    <option value="name">{{trans('plan.name')}}</option>
                                </select>
                            </div>
                        </div>
                        <div class="col-6 col-md-4">
                            <div class="form-group">
                                <label for="">{{trans('general.order')}}</label>
                                <select name="order" class="form-control" v-model="filterPlanForm.order">
                                    <option value="asc">{{trans('general.ascending')}}</option>
                                    <option value="desc">{{trans('general.descending')}}</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
        
        <div class="admin-list-table-card mt-2" v-if="hasPermission('list-plan') && plans.total">
            <div class="table-responsive">
                <table class="table admin-data-table admin-data-table--config-list mb-0">
                    <thead>
                        <tr>
                            <sortable-th field="name" :sort-by="filterPlanForm.sort_by" :order="filterPlanForm.order" @sort="sortList">{{trans('plan.name')}}</sortable-th>
                            <th>{{trans('plan.status')}}</th>
                            <th>{{trans('plan.max_student')}}</th>
                            <th>{{trans('plan.price')}}</th>
                            <th>{{trans('plan.description')}}</th>
                            <th class="admin-table-actions-cell">{{trans('general.actions')}}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="plan in plans.data" :key="plan.id">
                            <td>
                                <span>{{ plan.name }} <small class="text-soft">({{ plan.code }})</small></span>
                            </td>
                            <td>
                                <span v-if="plan.is_active" class="admin-status-badge admin-status-badge--success" :title="trans('plan.status')">
                                    <admin-icon name="check-circle"></admin-icon>
                                </span>
                                <span v-else class="admin-status-badge admin-status-badge--danger" :title="trans('plan.status')">
                                    <admin-icon name="x-circle"></admin-icon>
                                </span>
                            </td>
                            <td v-text="plan.options.max_student"></td>
                            <td>
                                <ul class="admin-table-role-list">
                                    <li v-for="(plan_price, ppIdx) in plan.plan_price" :key="ppIdx">
                                        {{trans('list.'+plan_price.frequency)}} {{formatCurrency(plan_price.amount,plan_price.currency)}}
                                    </li>
                                </ul>
                            </td>
                            <td v-html="nl2br(plan.description)"></td>
                            <td class="admin-table-actions-cell">
                                <table-row-action-dropdown>
                                    <b-dropdown-item-button v-if="hasPermission('edit-plan')" @click="editPlan(plan)">
                                        <admin-icon name="pencil"></admin-icon> {{ trans('general.edit') }}
                                    </b-dropdown-item-button>
                                    <b-dropdown-item-button
                                        v-if="hasPermission('delete-plan')"
                                        v-confirm="{ok: confirmDelete(plan)}"
                                        class="admin-row-actions__danger"
                                    >
                                        <admin-icon name="trash"></admin-icon> {{ trans('general.delete') }}
                                    </b-dropdown-item-button>
                                </table-row-action-dropdown>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="admin-table-pagination-wrap">
                <pagination-record :page-length.sync="filterPlanForm.page_length" :records="plans" @updateRecords="getPlans" @change.native="getPlans"></pagination-record>
            </div>
        </div>

        <div class="admin-list-table-card admin-empty-state mt-2" v-if="!plans.total" role="status">
            <div class="admin-empty-state__inner">
                <div class="admin-empty-state__icon-wrap" aria-hidden="true">
                    <admin-icon name="layers"></admin-icon>
                </div>
                <h4 class="admin-empty-state__title">{{ trans('plan.module_info_title') }}</h4>
                <p class="admin-empty-state__text">{{ trans('plan.module_info_description') }}</p>
                <button
                    v-if="hasPermission('create-currency')"
                    type="button"
                    class="btn btn-sm admin-toolbar-btn admin-toolbar-btn--solid mt-2"
                    v-b-modal.modal-1
                >
                    <admin-icon name="plus"></admin-icon> {{ trans('general.add_new') }}
                </button>
            </div>
        </div>

        <b-modal id="modal-1" hide-footer size="lg" centered modal-class="admin-modal-pro" content-class="admin-modal-pro__content">
            <template #modal-title>{{ trans('plan.plan') }} — {{ trans('general.add_new') }}</template>
            <plan-form @completed="onPlanCreated"></plan-form>
        </b-modal>
    </div>
</template>

<script>
    import planForm from './form'
    import { iconSvg } from '../../icons/admin-icons'

    export default {
        components : { planForm },
        data() {
            return {
                plans: {
                    total: 0,
                    data: []
                },
                filterPlanForm: {
                    name: '',
                    sort_by : 'name',
                    order: 'asc',
                    page_length: helper.getConfig('page_length')
                },
                showFilterPanel: false,
                showCreatePanel: false
            };
        },
        mounted(){
            this.getPlans();
        },
        methods: {
            hasPermission(permission){
                return helper.hasPermission(permission);
            },
            getPlans(page){
                if (typeof page !== 'number') {
                    page = 1;
                }
                let url = helper.getFilterURL(this.filterPlanForm);
                axios.get('/api/plan?page=' + page + url)
                    .then(response => response.data)
                    .then(response => this.plans = response)
                    .catch(error => {
                        helper.showDataErrorMsg(error);
                    });
            },
            onPlanCreated() {
                this.$bvModal.hide('modal-1');
                this.getPlans();
            },
            editPlan(plan){
                this.$router.push('/plan/'+plan.id+'/edit');
            },
            confirmDelete(plan){
                return dialog => this.deletePlan(plan);
            },
            deletePlan(plan){
                axios.delete('/api/plan/'+plan.id)
                    .then(response => response.data)
                    .then(response => {
                        toastr.success(response.message);
                        this.getPlans();
                    }).catch(error => {
                        helper.showDataErrorMsg(error);
                    });
            },
            getConfig(config){
                return helper.getConfig(config);
            },
            getStatus(plan){
                return plan.is_active
                    ? '<span class="admin-status-badge admin-status-badge--success">' + iconSvg('check-circle') + '</span>'
                    : '<span class="admin-status-badge admin-status-badge--danger">' + iconSvg('x-circle') + '</span>';
            },
            getDefault(plan){
                return plan.is_default ? ' <span class="admin-status-badge admin-status-badge--success">'+i18n.plan.default+'</span>' : '';
            },
            getFeatured(plan){
                return plan.is_featured ? ' <span class="admin-status-badge admin-status-badge--info">'+i18n.plan.featured+'</span>' : '';
            },
            nl2br(str){
                return helper.nl2br(str);
            },
            formatCurrency(amount, currency){
                return helper.formatCurrency(amount, currency);
            },
            sortList(field){
                if (this.filterPlanForm.sort_by === field) {
                    this.filterPlanForm.order = this.filterPlanForm.order === 'asc' ? 'desc' : 'asc';
                } else {
                    this.filterPlanForm.sort_by = field;
                    this.filterPlanForm.order = 'asc';
                }
            }
        },
        watch: {
            filterPlanForm: {
                handler(val){
                    this.getPlans();
                },
                deep: true
            }
        }
    }
</script>
